import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/LocationLicenseOverview/components/Remark/Remark.scss";
import { formatDate } from "utils/dateHelperFunctions";

import type { RemarkResponse } from "modules/LocationLicenseOverview/queryKeys";
import type { ChangeEvent } from "react";

type RemarkProps = {
  remarkData: RemarkResponse;
  onUpdateRemark: (remark: string) => void;
};

export const Remark = ({ remarkData, onUpdateRemark }: RemarkProps) => {
  const { t } = useTranslation();
  const canUpdateRemark = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactAccounts);

  const [inputValue, setInputValue] = useState(remarkData.exact_remark || "");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setInputValue(remarkData.exact_remark);
  }, [remarkData.exact_remark]);

  const { exact_remark, exact_remark_updated_by = "", exact_remark_updated_on = "" } = remarkData;

  const startRemarkEdit = () => {
    const inputElement = inputRef.current;

    if (inputElement) {
      const valueLength = inputElement.value.length;
      inputElement.setSelectionRange(valueLength, valueLength);
      inputElement.focus();
    }
  };

  const handleInputFocues = () => {
    if (!canUpdateRemark) return;
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    if (canUpdateRemark && inputRef.current?.value !== exact_remark) onUpdateRemark(inputValue);
  };

  const handleInputChange = (evt: ChangeEvent<HTMLTextAreaElement>) => setInputValue(evt.target.value);

  useEffect(() => {
    if (isInputFocused) startRemarkEdit();
  }, [isInputFocused]);

  return (
    <div className="Remark">
      {isInputFocused || !exact_remark ? (
        <textarea
          className="remark-input"
          ref={inputRef}
          value={inputValue}
          readOnly={!canUpdateRemark}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          placeholder={t("v8_remark", "Remark")}
        />
      ) : (
        <Popup
          trigger={
            <div onClick={handleInputFocues} className="remark-text-container">
              {exact_remark}
            </div>
          }
          content={
            <div>
              {exact_remark}
              {exact_remark_updated_by && exact_remark_updated_on && (
                <div className="location-licenses-remark-info">{`${t("v8_updated_by", "Updated by")} ${exact_remark_updated_by} ${t("v8_on", "on")} ${formatDate(new Date(exact_remark_updated_on), DATE_FORMATS_FNS.dateMonthYearTime)}`}</div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};
